import * as React from "react";

export interface MyBagItemsRunningStateConsumerInterface {
  workingModificationsToQuantity: MyBagItemsRunningState_WorkingModificationsToQuantity[];
}
const defaultValue = {} as unknown as MyBagItemsRunningStateConsumerInterface;
const MyBagItemsRunningStateContext = React.createContext(defaultValue);

export interface MyBagItemsRunningState_RequestChangeQuantity
  extends Omit<
    MyBagItemsRunningState_WorkingModificationsToQuantity,
    "status"
  > {}
export interface MyBagItemsRunningState_UnregisterRequest
  extends Omit<
    MyBagItemsRunningState_WorkingModificationsToQuantity,
    "status" | "quantity"
  > {}
export interface MyBagItemsRunningState_ControllerInterface {
  registerWorkingModificationToQuantity: (
    value: MyBagItemsRunningState_RequestChangeQuantity
  ) => void;
  registerStatusForWorkingModificationToQuantity: (
    request: MyBagItemsRunningState_RequestChangeQuantity,
    status: `${MyBagItemsRunningState_WorkingModificationsToQuantity_Status}`
  ) => void;
  unregisterWorkingModificationToQuantity: (
    request: MyBagItemsRunningState_UnregisterRequest
  ) => void;
}
const controllerDefaultValue =
  {} as unknown as MyBagItemsRunningState_ControllerInterface;
const MyBagItemsRunningState_ControllerContext = React.createContext(
  controllerDefaultValue
);

export enum MyBagItemsRunningState_WorkingModificationsToQuantity_Status {
  approved = "approved",
  pending = "pending",
  rejected = "rejected",
  rejectedOutOfStock = "rejectedOutOfStock",
}
export interface MyBagItemsRunningState_WorkingModificationsToQuantity {
  cartId: string;
  cartItemToUpdateId: string;
  quantity: number;
  status: `${MyBagItemsRunningState_WorkingModificationsToQuantity_Status}`;
}

export const MyBagItemsRunningStateProvider: React.FunctionComponent = (
  props
) => {
  const [workingModificationsToQuantity, setWorkingModificationsToQuantity] =
    React.useState<MyBagItemsRunningState_WorkingModificationsToQuantity[]>([]);

  const registerWorkingModificationToQuantity: MyBagItemsRunningState_ControllerInterface["registerWorkingModificationToQuantity"] =
    React.useCallback(
      (value) => {
        const preexistingSameRequest = workingModificationsToQuantity.find(
          (item) =>
            item.cartId === value.cartId &&
            item.cartItemToUpdateId === value.cartItemToUpdateId
        );
        if (preexistingSameRequest) {
          setWorkingModificationsToQuantity(
            workingModificationsToQuantity.map((item) => {
              if (
                item.cartId === value.cartId &&
                item.cartItemToUpdateId === value.cartItemToUpdateId &&
                item.quantity !== value.quantity
              ) {
                return {
                  ...item,
                  quantity: value.quantity,
                  status:
                    MyBagItemsRunningState_WorkingModificationsToQuantity_Status.pending,
                };
              }
              return item;
            })
          );
        } else {
          setWorkingModificationsToQuantity([
            ...workingModificationsToQuantity,
            {
              ...value,
              status:
                MyBagItemsRunningState_WorkingModificationsToQuantity_Status.pending,
            },
          ]);
        }
      },
      [workingModificationsToQuantity]
    );

  const registerStatusForWorkingModificationToQuantity: MyBagItemsRunningState_ControllerInterface["registerStatusForWorkingModificationToQuantity"] =
    React.useCallback(
      (request, status) => {
        setWorkingModificationsToQuantity((workingModificationsToQuantity) =>
          workingModificationsToQuantity.map((item) => {
            if (
              item.cartId === request.cartId &&
              item.cartItemToUpdateId === request.cartItemToUpdateId &&
              item.quantity === request.quantity
            ) {
              return {
                ...item,
                status,
              };
            }
            return item;
          })
        );
      },
      [workingModificationsToQuantity]
    );

  const unregisterWorkingModificationToQuantity: MyBagItemsRunningState_ControllerInterface["unregisterWorkingModificationToQuantity"] =
    React.useCallback(
      (request) => {
        setWorkingModificationsToQuantity(
          workingModificationsToQuantity.filter(
            (item) =>
              !(
                item.cartId === request.cartId &&
                item.cartItemToUpdateId === request.cartItemToUpdateId
              )
          )
        );
      },
      [workingModificationsToQuantity]
    );

  const value: MyBagItemsRunningStateConsumerInterface = React.useMemo(
    () => ({
      workingModificationsToQuantity,
    }),
    [workingModificationsToQuantity]
  );
  const controllerValue: MyBagItemsRunningState_ControllerInterface =
    React.useMemo(
      () => ({
        registerWorkingModificationToQuantity,
        registerStatusForWorkingModificationToQuantity,
        unregisterWorkingModificationToQuantity,
      }),
      [
        registerWorkingModificationToQuantity,
        registerStatusForWorkingModificationToQuantity,
        unregisterWorkingModificationToQuantity,
      ]
    );
  return (
    <MyBagItemsRunningStateContext.Provider value={value}>
      <MyBagItemsRunningState_ControllerContext.Provider
        value={controllerValue}
      >
        {props.children || []}
      </MyBagItemsRunningState_ControllerContext.Provider>
    </MyBagItemsRunningStateContext.Provider>
  );
};

export const useMyBagItemsRunningState = () => {
  const value = React.useContext(MyBagItemsRunningStateContext);
  if (value === defaultValue) {
    throw new Error(
      "useMyBagItemsRunningState must be used within a MyBagItemsRunningStateProvider"
    );
  }
  return value;
};

export const useMyBagItemsRunningState_asController = () => {
  const value = React.useContext(MyBagItemsRunningState_ControllerContext);
  if (value === controllerDefaultValue) {
    throw new Error(
      "useMyBagItemsRunningState_asController must be used within a MyBagItemsRunningStateProvider"
    );
  }
  return value;
};
